import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Plus, Check, Clock, AlertCircle, Gift, DollarSign, MessageCircle, Phone, Calendar, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { decryptId } from '../utils/urlEncryption';
import { PaymentTypeSelector, CashPaymentModal, CardPaymentModal, UPIPaymentModal } from '../components/PaymentModals';
import { BankTransferModal, OldGoldModal } from '../components/PaymentModalsExtended';
import { MetalCreditModal } from '../components/MetalCreditModalNew';
import KittyThermalPrint from '../components/KittyThermalPrint';
import ItemReceiptPrint from '../components/ItemReceiptPrint';
import ItemReceiptSelector from '../components/ItemReceiptSelector';
import { openPrintWindow } from '../utils/printWindow';
import { formatDateIST, formatDateOnlyIST, formatTimeIST } from '../utils/dateUtils';
import '../styles/print.css';

const KittyEnrollmentDetail = ({ user }) => {
  const navigate = useNavigate();
  const { enrollmentId: encryptedEnrollmentId } = useParams();
  
  // Decrypt the enrollment ID from URL
  const enrollmentId = decryptId(encryptedEnrollmentId);
  
  const [enrollment, setEnrollment] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  
  // Payment modal states
  const [showPaymentTypeSelector, setShowPaymentTypeSelector] = useState(false);
  const [activePaymentModal, setActivePaymentModal] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule'); // 'schedule', 'timeline', 'revisions'
  const [currentRate, setCurrentRate] = useState('');
  
  // WhatsApp modal state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showItemReceiptSelector, setShowItemReceiptSelector] = useState(false);
  
  // View mode
  const [viewMode, setViewMode] = useState('schedule'); // 'schedule' | 'timeline'

  useEffect(() => {
    loadEnrollment();
  }, [enrollmentId, currentRate]);

  const loadEnrollment = async () => {
    try {
      const res = await axiosInstance.get(`/kitty/enrollments/${enrollmentId}`);
      setEnrollment(res.data);
      
      // Load customer
      const custRes = await axiosInstance.get(`/customers/${res.data.customer_id}`);
      setCustomer(custRes.data);
      
      // Load scheme
      const schemeRes = await axiosInstance.get(`/kitty/schemes/${res.data.scheme_id}`);
      setScheme(schemeRes.data);
    } catch (err) {
      toast.error('Error loading enrollment');
      navigate('/kitty');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = (period) => {
    if (period.entry_type === 'Bonus') {
      toast.error('Cannot manually pay bonus periods');
      return;
    }
    
    if (period.status === 'Paid') {
      toast.info('Period already paid');
      return;
    }
    
    setSelectedPeriod(period);
    setShowPaymentTypeSelector(true);
  };

  const handlePaymentSave = async (paymentData) => {
    if (!selectedPeriod) {
      toast.error('No period selected');
      return;
    }

    try {
      const expectedAmount = selectedPeriod.expected_amount || selectedPeriod.due_amount || 0;
      const currentPaid = selectedPeriod.actual_amount_paid || 0;
      const paymentAmount = paymentData.amount || expectedAmount;
      const newTotalPaid = currentPaid + paymentAmount;
      
      // Check for shortfall and show warning
      if (newTotalPaid < expectedAmount) {
        const shortfall = expectedAmount - newTotalPaid;
        const proceed = window.confirm(
          `⚠️ PARTIAL PAYMENT WARNING\n\n` +
          `Expected Amount: ₹${expectedAmount}\n` +
          `Already Paid: ₹${currentPaid}\n` +
          `New Payment: ₹${paymentAmount}\n` +
          `Total After Payment: ₹${newTotalPaid}\n` +
          `Remaining Shortfall: ₹${shortfall}\n\n` +
          `This period will remain "Due" until full payment is received.\n` +
          `Maturity will be blocked until all shortfalls are cleared.\n\n` +
          `Do you want to proceed with this partial payment?`
        );
        
        if (!proceed) {
          return;
        }
      }

      const paymentEntry = {
        period_index: selectedPeriod.period_index,
        amount: paymentAmount,
        payment_type: paymentData.payment_type || paymentData.mode || 'Cash',
        payment_date: paymentData.date || new Date().toISOString(),
        reference: paymentData.reference || '',
        note: paymentData.note || '',
        rate_used: currentRate || paymentData.rate_used || scheme?.locked_rate || 0,
        // Pass through all payment-specific fields
        card_last4: paymentData.card_last4,
        bank_name: paymentData.bank_name,
        upi_id: paymentData.upi_handle || paymentData.upi_id,  // Map upi_handle to upi_id
        subtype: paymentData.subtype,
        utr_ref: paymentData.utr_ref || paymentData.upi_ref,  // Map upi_ref to utr_ref for reference
        payer_acct: paymentData.payer_acct,
        cheque_no: paymentData.cheque_no,
        cheque_bank: paymentData.cheque_bank,
        cheque_date: paymentData.cheque_date,
        items: paymentData.items || [],
        deposits: paymentData.deposits || [],
        fine_grams: paymentData.fine_grams
      };
      
      await axiosInstance.post(`/kitty/enrollments/${enrollmentId}/pay`, paymentEntry);
      
      if (newTotalPaid < expectedAmount) {
        toast.warning(`Partial payment of ₹${paymentAmount} recorded. Remaining shortfall: ₹${expectedAmount - newTotalPaid}`);
      } else {
        toast.success('Payment recorded successfully!');
      }
      
      // Reset and reload
      setActivePaymentModal(null);
      setShowPaymentTypeSelector(false);
      setSelectedPeriod(null);
      loadEnrollment();
    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.response?.data?.detail || 'Error recording payment');
    }
  };

  const handleRedeem = async () => {
    // Check for shortfall before allowing redemption
    if (enrollment.has_shortfall || enrollment.total_shortfall > 0) {
      toast.error(
        `❌ REDEMPTION BLOCKED\n\nTotal Shortfall: ₹${enrollment.total_shortfall}\n\nPlease clear all outstanding payments before redemption.`,
        { duration: 5000 }
      );
      return;
    }
    
    if (!window.confirm('Proceed with redemption? This action cannot be undone.')) return;
    
    navigate(`/kitty/enrollments/${enrollmentId}/redeem`);
  };

  // WhatsApp - Show modal with options
  const handleWhatsAppClick = () => {
    if (!customer?.mobile) {
      toast.error('Customer mobile number not available');
      return;
    }
    setShowWhatsAppModal(true);
  };

  // Send payment reminder
  const sendPaymentReminder = () => {
    const nextDue = enrollment.schedule?.find(entry => 
      entry.status === 'Due' && entry.entry_type === 'CustomerPay'
    );
    
    if (!nextDue) {
      toast.info('No pending payments');
      return;
    }
    
    const dueDate = nextDue.due_date ? formatDateOnlyIST(nextDue.due_date) : 'N/A';
    const expectedAmount = nextDue.expected_amount || nextDue.due_amount || 0;
    
    let mobile = customer.mobile.replace(/\s/g, '').replace(/^\+91/, '').replace(/^91/, '');
    if (mobile.length === 10) {
      mobile = '91' + mobile;
    }
    
    const message = encodeURIComponent(
      `Dear ${customer.name},\n\n` +
      `This is a friendly reminder for your Kitty payment.\n\n` +
      `Scheme: ${scheme?.scheme_name || 'Kitty'}\n` +
      `Enrollment: ${enrollment.enrollment_id}\n` +
      `Period: ${nextDue?.period_number || 'N/A'}\n` +
      `Due Date: ${dueDate}\n` +
      `Amount: ₹${typeof expectedAmount === 'number' ? expectedAmount.toLocaleString('en-IN') : expectedAmount}\n\n` +
      `Please make the payment at your earliest convenience.\n\n` +
      `Thank you!`
    );
    
    window.open(`https://wa.me/${mobile}?text=${message}`, '_blank');
    setShowWhatsAppModal(false);
  };

  // Send full summary
  const sendFullSummary = async () => {
    const message = generateThermalSummaryText();
    
    let mobile = customer.mobile.replace(/\s/g, '').replace(/^\+91/, '').replace(/^91/, '');
    if (mobile.length === 10) {
      mobile = '91' + mobile;
    }
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
    setShowWhatsAppModal(false);
  };

  // Generate thermal summary text for WhatsApp
  const generateThermalSummaryText = () => {
    const formatCurrency = (amount) => {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString('en-IN', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    };

    // Calculate totals
    const totalPeriods = enrollment.schedule?.length || 0;
    const paidPeriods = enrollment.schedule?.filter(p => p.status === 'Paid').length || 0;
    const progressPercent = totalPeriods > 0 ? ((paidPeriods / totalPeriods) * 100).toFixed(1) : 0;

    const amountCollected = enrollment.schedule?.reduce((sum, period) => {
      return sum + (parseFloat(period.actual_amount_paid) || 0);
    }, 0) || 0;

    const gramsAccumulated = enrollment.schedule?.reduce((sum, period) => {
      return sum + (parseFloat(period.grams_accumulated) || 0);
    }, 0) || 0;

    let msg = '';
    msg += '═══════════════════════════════════\n';
    msg += '       KITTY ENROLLMENT\n';
    msg += '═══════════════════════════════════\n';
    msg += `Enrollment ID: ${enrollment.enrollment_id}\n`;
    msg += `Date: ${formatDateOnlyIST(enrollment.enrollment_date || enrollment.created_at)}\n\n`;

    msg += '───────────────────────────────────\n';
    msg += 'CUSTOMER\n';
    msg += '───────────────────────────────────\n';
    msg += `Name: ${customer.name}\n`;
    if (customer.mobile) msg += `Mobile: ${customer.mobile}\n`;
    msg += '\n';

    msg += '═══════════════════════════════════\n';
    msg += 'SUMMARY\n';
    msg += '═══════════════════════════════════\n';
    msg += `Scheme: ${scheme?.scheme_name || 'N/A'}\n`;
    msg += `Progress: ${paidPeriods}/${totalPeriods} periods (${progressPercent}%)\n`;
    msg += `Amount Collected: ₹${formatCurrency(amountCollected)}\n`;
    msg += `Grams Accumulated: ${gramsAccumulated.toFixed(3)}g\n`;
    if (enrollment.maturity_date) {
      msg += `Maturity Date: ${formatDateOnlyIST(enrollment.maturity_date)}\n`;
    }
    msg += '\n';

    msg += '───────────────────────────────────\n';
    msg += 'PAYMENT SCHEDULE\n';
    msg += '───────────────────────────────────\n\n';

    if (enrollment.schedule && enrollment.schedule.length > 0) {
      enrollment.schedule.forEach((period) => {
        const status = period.status === 'Paid' ? '✓ PAID' : period.status === 'Due' ? '⏱ DUE' : '○ PENDING';
        const periodPayments = period.payment_details || [];
        
        msg += `Period #${period.period_number}\n`;
        msg += `Due: ${formatDateOnlyIST(period.due_date)}\n`;
        msg += `Amount: ₹${formatCurrency(period.expected_amount || period.due_amount || 0)}\n`;
        msg += `Status: ${status}\n`;
        
        // Add payment details if paid
        if (period.status === 'Paid' && periodPayments.length > 0) {
          periodPayments.forEach(payment => {
            msg += '  ───────────────\n';
            msg += `  ${payment.payment_type || 'Cash'} Payment\n`;
            
            if (payment.payment_date) msg += `  Date: ${formatDateOnlyIST(payment.payment_date)}\n`;
            if (payment.amount) msg += `  Amount: ₹${formatCurrency(payment.amount)}\n`;
            
            // UPI
            if (payment.payment_type === 'UPI') {
              if (payment.upi_id) msg += `  UPI ID: ${payment.upi_id}\n`;
              if (payment.reference) msg += `  ${payment.reference}\n`;
            }
            
            // Card
            if (payment.payment_type === 'Card') {
              if (payment.card_bank_name) msg += `  Bank: ${payment.card_bank_name}\n`;
              if (payment.card_last4) msg += `  Card: •••• ${payment.card_last4}\n`;
              if (payment.reference) msg += `  Ref: ${payment.reference}\n`;
            }
            
            // Bank Transfer
            if (payment.payment_type === 'Bank Transfer') {
              if (payment.bank_subtype) msg += `  Type: ${payment.bank_subtype.toUpperCase()}\n`;
              if (payment.bank_name) msg += `  Bank: ${payment.bank_name}\n`;
              if (payment.utr_ref) msg += `  UTR: ${payment.utr_ref}\n`;
              if (payment.cheque_no) msg += `  Cheque: ${payment.cheque_no}\n`;
              if (payment.cheque_bank) msg += `  Cheque Bank: ${payment.cheque_bank}\n`;
              if (payment.payer_acct_last4) msg += `  Acct: •••• ${payment.payer_acct_last4}\n`;
            }
            
            // Old Gold
            if (payment.payment_type === 'Old Gold') {
              if (payment.old_gold_gross_wt) msg += `  Gross: ${payment.old_gold_gross_wt}g\n`;
              if (payment.old_gold_net_wt) msg += `  Net: ${payment.old_gold_net_wt}g\n`;
              if (payment.old_gold_purity) msg += `  Purity: ${payment.old_gold_purity}%\n`;
              if (payment.old_gold_fine_wt) msg += `  Fine: ${payment.old_gold_fine_wt}g\n`;
            }
            
            // Metal Credit
            if (payment.payment_type === 'Metal Credit' && payment.metal_deposits) {
              try {
                const deposits = JSON.parse(payment.metal_deposits);
                deposits.forEach((dep, idx) => {
                  msg += `  Deposit ${idx + 1}: ${dep.weight}g @ ₹${formatCurrency(dep.rate)}/g\n`;
                });
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
            
            if (payment.note) {
              msg += `  Note: ${payment.note}\n`;
            }
          });
        }
        
        msg += '\n';
      });
    }

    msg += '═══════════════════════════════════\n';
    msg += 'Thank you!\n';

    return msg;
  };

  // Print handler
  // Extract Old Gold and Metal Credit items from Kitty payments
  const getOldGoldAndMetalCreditItems = () => {
    const items = [];
    
    if (enrollment?.payment_schedule) {
      enrollment.payment_schedule.forEach((period) => {
        if (period.payment_details && Array.isArray(period.payment_details)) {
          period.payment_details.forEach((payment) => {
            // Old Gold items
            if (payment.payment_type === 'Old Gold') {
              items.push({
                gross_wt: payment.old_gold_gross_wt,
                net_wt: payment.old_gold_net_wt,
                less_wt: payment.old_gold_less_wt,
                purity: payment.old_gold_purity,
                fine_wt: payment.old_gold_fine_wt,
                rate: payment.rate_used,
                value: payment.amount,
                payment_id: payment.entry_id,
                payment_date: payment.payment_date,
                type: 'old_gold'
              });
            }
            
            // Metal Credit items
            if (payment.payment_type === 'Metal Credit' && payment.metal_deposits) {
              try {
                const deposits = typeof payment.metal_deposits === 'string' 
                  ? JSON.parse(payment.metal_deposits) 
                  : payment.metal_deposits;
                
                if (Array.isArray(deposits)) {
                  deposits.forEach((deposit) => {
                    items.push({
                      ...deposit,
                      deposits: [deposit],
                      payment_id: payment.entry_id,
                      payment_date: payment.payment_date,
                      type: 'metal_credit'
                    });
                  });
                }
              } catch (e) {
                console.error('Error parsing metal deposits:', e);
              }
            }
          });
        }
      });
    }
    
    return items;
  };

  const handlePrintItemReceipts = () => {
    const items = getOldGoldAndMetalCreditItems();
    if (items.length === 0) {
      toast.error('No Old Gold or Metal Credit items found');
      return;
    }
    setShowItemReceiptSelector(true);
  };

  const handlePrintSelectedItems = (selectedIndices) => {
    let combinedHTML = '';
    selectedIndices.forEach((index) => {
      const receiptElement = document.getElementById(`item-receipt-${index}`);
      if (receiptElement) {
        combinedHTML += receiptElement.outerHTML;
      }
    });
    if (combinedHTML) {
      openPrintWindow(combinedHTML, 'Item Receipts', { pageSize: 'thermal' });
    } else {
      toast.error('No receipt content found.');
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('kitty-thermal-print-content');
    if (printContent) {
      openPrintWindow(printContent.outerHTML, `Kitty ${enrollment?.enrollment_id} - Thermal Print`, { pageSize: 'thermal' });
    } else {
      toast.error('Print content not found. Please try again.');
    }
  };
  
  const handleDelete = async () => {
    const confirmMsg = `⚠️ PERMANENT DELETE WARNING\n\n` +
      `This will permanently delete:\n` +
      `- Enrollment: ${enrollment.enrollment_id}\n` +
      `- Customer: ${customer?.name}\n` +
      `- All payment records\n` +
      `- Complete history\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type "DELETE" to confirm:`;
    
    const userInput = window.prompt(confirmMsg);
    
    if (userInput !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }
    
    try {
      await axiosInstance.delete(`/kitty/enrollments/${enrollmentId}`);
      toast.success('Enrollment deleted permanently');
      navigate('/kitty');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting enrollment');
    }
  };
  
  const handleDeletePayment = async (paymentId, periodIndex, amount) => {
    const confirmMsg = `Are you sure you want to delete this payment?\n\n` +
      `Period: ${periodIndex}\n` +
      `Amount: ₹${amount?.toLocaleString('en-IN')}\n` +
      `Payment ID: ${paymentId}\n\n` +
      `This will reverse the amount from the period and update revision history.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/kitty/enrollments/${enrollmentId}/payments/${paymentId}`);
      toast.success('Payment deleted and amounts reversed');
      loadEnrollment(); // Reload to show updated state
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting payment');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Paid') return <Check className="w-4 h-4 text-green-600" />;
    if (status === 'Late') return <AlertCircle className="w-4 h-4 text-red-600" />;
    if (status === 'Bonus_Applied') return <Gift className="w-4 h-4 text-green-600" />;
    return <Clock className="w-4 h-4 text-amber-600" />;
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Paid': 'bg-green-100 text-green-700',
      'Due': 'bg-amber-100 text-amber-700',
      'Late': 'bg-red-100 text-red-700',
      'Bonus_Applied': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Get paid entries for timeline
  const getPaidEntries = () => {
    if (!enrollment?.schedule) return [];
    return enrollment.schedule
      .filter(entry => entry.status === 'Paid' || entry.status === 'Bonus_Applied')
      .sort((a, b) => new Date(b.paid_date || b.due_date) - new Date(a.paid_date || a.due_date));
  };

  // Show loading while checking access
  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  // If no access, component will redirect (handled by hook)
  if (!hasAccess) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-amber-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Loading enrollment...</p>
        </div>
      </div>
    );
  }

  if (!enrollment) return null;

  const paidEntries = getPaidEntries();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-32">
      {/* Header with Action Buttons */}
      <div className="bg-white border-b-2 border-amber-200 shadow-sm">
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          {/* Back Button and Enrollment ID */}
          <div className="flex items-center gap-2 mb-3">
            <button 
              onClick={() => navigate('/kitty')} 
              className="p-2 hover:bg-gray-100 rounded-lg touch-target flex-shrink-0"
              title="Back to Kitty"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 truncate">{enrollment.enrollment_id}</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {customer?.name} • {scheme?.name}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 mb-2">
            {(user?.role === 'Admin' || user?.role === 'Manager') && (
              <button
                onClick={handleDelete}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-xs sm:text-sm touch-target"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Delete Enrollment</span>
              </button>
            )}
          </div>
          
          {/* Status Badge */}
          <div className="flex gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
              enrollment.status === 'Active' ? 'bg-green-100 text-green-700' :
              enrollment.status === 'Redeemed' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {enrollment.status}
            </span>
            {enrollment.has_shortfall && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                Shortfall: ₹{enrollment.total_shortfall?.toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Provisional Changes Watermark */}
        {enrollment.has_provisional_changes && (
          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-3 flex items-start gap-3" data-testid="provisional-banner">
            <div className="bg-amber-400 rounded-full p-1.5 flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-amber-800">Provisional Changes</p>
              <p className="text-xs text-amber-700">
                Changes made by <span className="font-semibold">{enrollment.provisional_by}</span> are pending admin approval.
                Data is live but not yet confirmed.
              </p>
            </div>
          </div>
        )}
        <div className="premium-card border-2 border-blue-200 p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
          <h3 className="text-sm font-bold text-gray-800 mb-2">Customer Details</h3>
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-gray-800">{customer?.name}</p>
              {customer?.mobile && (
                <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                  <Phone className="w-4 h-4" />
                  {customer.mobile}
                </p>
              )}
              {customer?.address && (
                <p className="text-xs text-gray-500 mt-1">{customer.address}</p>
              )}
            </div>
            {/* Action Buttons */}
            {customer?.mobile && enrollment.status === 'Active' && (
              <div className="flex gap-2">
                <button
                  onClick={handlePrint}
                  className="px-3 py-2 bg-blue-500 text-white rounded-lg text-xs font-semibold hover:bg-blue-600 transition-all flex items-center gap-1"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={handleWhatsAppClick}
                  className="px-3 py-2 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-all flex items-center gap-1"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Summary Card */}
        <div className="premium-card border-2 border-amber-200 p-4 bg-gradient-to-r from-amber-50 to-orange-50">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Summary</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-600">Scheme</p>
              <p className="font-semibold">{scheme?.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Progress</p>
              <p className="font-semibold">{enrollment.periods_paid} / {enrollment.total_periods}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Amount Collected</p>
              <p className="font-semibold text-green-600">₹{enrollment.total_amount_collected?.toLocaleString('en-IN') || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Grams Accumulated</p>
              <p className="font-semibold text-amber-600">{enrollment.total_grams_accumulated?.toFixed(3) || 0}g</p>
            </div>
          </div>
          
          {enrollment.maturity_date && (
            <div className="mt-3 pt-3 border-t border-amber-300 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-600" />
              <div>
                <p className="text-xs text-gray-600">Maturity Date</p>
                <p className="font-semibold">{formatDateOnlyIST(enrollment.maturity_date)}</p>
              </div>
            </div>
          )}
        </div>

        {/* View Toggle */}
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => setViewMode('schedule')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              viewMode === 'schedule' 
                ? 'bg-amber-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Payment Schedule
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              viewMode === 'timeline' 
                ? 'bg-amber-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Payment Timeline
          </button>
          <button
            onClick={() => setViewMode('revisions')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              viewMode === 'revisions' 
                ? 'bg-amber-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Revision History
          </button>
        </div>

        {/* Schedule View */}
        {viewMode === 'schedule' && (
          <div className="premium-card border-2 border-amber-200 p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3">Payment Schedule</h3>
            
            <div className="space-y-2">
              {enrollment.schedule?.map((entry, idx) => (
                <div key={idx} className={`border-2 rounded-lg p-3 ${
                  entry.entry_type === 'Bonus' ? 'border-green-200 bg-green-50' :
                  entry.status === 'Paid' ? 'border-green-200 bg-green-50' :
                  entry.status === 'Late' ? 'border-red-200 bg-red-50' :
                  'border-amber-200 bg-white'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">Period {entry.period_index}</span>
                      {getStatusIcon(entry.status)}
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadge(entry.status)}`}>
                        {entry.status.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {entry.entry_type === 'CustomerPay' && entry.status === 'Due' && (
                      <button
                        onClick={() => handlePaymentClick(entry)}
                        className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-all"
                      >
                        Pay
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-600">Due Date:</span>
                      <span className="ml-1 font-medium">{formatDateOnlyIST(entry.due_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Type:</span>
                      <span className="ml-1 font-medium">{entry.entry_type === 'Bonus' ? '🎁 Bonus' : '💰 Payment'}</span>
                    </div>
                    
                    {entry.expected_amount && (
                      <div>
                        <span className="text-gray-600">Expected:</span>
                        <span className="ml-1 font-medium">₹{entry.expected_amount?.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    
                    {entry.expected_grams && (
                      <div>
                        <span className="text-gray-600">Expected:</span>
                        <span className="ml-1 font-medium">{entry.expected_grams}g</span>
                      </div>
                    )}
                    
                    {entry.actual_amount_paid && (
                      <div>
                        <span className="text-gray-600">Paid:</span>
                        <span className="ml-1 font-medium text-green-700">₹{entry.actual_amount_paid?.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    
                    {entry.actual_grams_credited && (
                      <div>
                        <span className="text-gray-600">Credited:</span>
                        <span className="ml-1 font-medium text-amber-700">{entry.actual_grams_credited?.toFixed(3)}g</span>
                      </div>
                    )}
                    
                    {entry.rate_used && (
                      <div>
                        <span className="text-gray-600">Rate:</span>
                        <span className="ml-1 font-medium">₹{entry.rate_used}/g</span>
                      </div>
                    )}
                    
                    {entry.paid_date && (
                      <div>
                        <span className="text-gray-600">Paid On:</span>
                        <span className="ml-1 font-medium">{formatDateOnlyIST(entry.paid_date)}</span>
                      </div>
                    )}
                    
                    {entry.has_shortfall && (
                      <div className="col-span-2">
                        <span className="text-red-600 font-semibold">⚠️ Shortfall:</span>
                        <span className="ml-1 font-medium text-red-700">₹{entry.shortfall_amount?.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Payment Details Breakdown */}
                  {entry.payment_details && entry.payment_details.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">💳 Payment History ({entry.payment_details.length})</p>
                      <div className="space-y-2">
                        {entry.payment_details.map((payment, pidx) => (
                          <div key={pidx} className="bg-white rounded-lg p-3 text-xs border-2 border-gray-200 shadow-sm">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 font-bold text-[11px]">
                                  {payment.payment_type?.toUpperCase() || 'CASH'}
                                </span>
                                <span className="font-bold text-green-700 text-sm">₹{payment.amount?.toLocaleString('en-IN')}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <span className="text-gray-500 text-[10px] font-medium block">
                                    {formatDateOnlyIST(payment.payment_date || payment.created_at)}
                                  </span>
                                  <span className="text-gray-500 text-[10px] font-medium block">
                                    {formatTimeIST(payment.payment_date || payment.created_at)}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleDeletePayment(payment.payment_id, entry.period_index, payment.amount)}
                                  className="p-1.5 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-700 transition-colors"
                                  title="Delete Payment"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            
                            {/* Payment Type Specific Details */}
                            <div className="space-y-1">
                              {/* CARD Payment */}
                              {payment.payment_type === 'card' && (
                                <>
                                  {payment.card_last4 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Card Number:</span>
                                      <span className="font-medium">****{payment.card_last4}</span>
                                    </div>
                                  )}
                                  {payment.card_bank_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Bank:</span>
                                      <span className="font-medium">{payment.card_bank_name}</span>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {/* UPI Payment */}
                              {payment.payment_type === 'upi' && payment.upi_id && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">UPI ID:</span>
                                  <span className="font-medium">{payment.upi_id}</span>
                                </div>
                              )}
                              
                              {/* BANK Transfer */}
                              {payment.payment_type === 'bank' && (
                                <>
                                  {payment.bank_subtype && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Type:</span>
                                      <span className="font-medium uppercase">{payment.bank_subtype}</span>
                                    </div>
                                  )}
                                  {payment.utr_ref && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">UTR/Ref:</span>
                                      <span className="font-medium">{payment.utr_ref}</span>
                                    </div>
                                  )}
                                  {payment.bank_name && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Bank:</span>
                                      <span className="font-medium">{payment.bank_name}</span>
                                    </div>
                                  )}
                                  {payment.payer_acct_last4 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Account:</span>
                                      <span className="font-medium">****{payment.payer_acct_last4}</span>
                                    </div>
                                  )}
                                  {payment.cheque_no && (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Cheque No:</span>
                                        <span className="font-medium">{payment.cheque_no}</span>
                                      </div>
                                      {payment.cheque_bank && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Cheque Bank:</span>
                                          <span className="font-medium">{payment.cheque_bank}</span>
                                        </div>
                                      )}
                                      {payment.cheque_date && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Cheque Date:</span>
                                          <span className="font-medium">{formatDateOnlyIST(payment.cheque_date)}</span>
                                        </div>
                                      )}
                                      {payment.cheque_status && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Status:</span>
                                          <span className={`font-medium ${payment.cheque_status === 'Cleared' ? 'text-green-600' : 'text-orange-600'}`}>
                                            {payment.cheque_status}
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                              
                              {/* OLD GOLD */}
                              {payment.payment_type === 'old-gold' && (
                                <>
                                  {payment.old_gold_gross_wt && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Gross Weight:</span>
                                      <span className="font-medium">{payment.old_gold_gross_wt}g</span>
                                    </div>
                                  )}
                                  {payment.old_gold_less_wt && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Less Weight:</span>
                                      <span className="font-medium">{payment.old_gold_less_wt}g</span>
                                    </div>
                                  )}
                                  {payment.old_gold_net_wt && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Net Weight:</span>
                                      <span className="font-medium text-amber-700">{payment.old_gold_net_wt}g</span>
                                    </div>
                                  )}
                                  {payment.old_gold_purity && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Purity:</span>
                                      <span className="font-medium">{payment.old_gold_purity}%</span>
                                    </div>
                                  )}
                                  {payment.old_gold_fine_wt && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Fine Weight:</span>
                                      <span className="font-medium text-green-700">{payment.old_gold_fine_wt}g</span>
                                    </div>
                                  )}
                                  {payment.rate_used && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Rate:</span>
                                      <span className="font-medium">₹{payment.rate_used}/g</span>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {/* Common fields for all types */}
                              {payment.note && (
                                <div className="mt-2 pt-2 border-t border-gray-100">
                                  <p className="text-gray-600 text-[10px]"><span className="font-semibold">Note:</span> {payment.note}</p>
                                </div>
                              )}
                              {payment.reference && payment.reference !== payment.note && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Reference:</span>
                                  <span className="font-medium">{payment.reference}</span>
                                </div>
                              )}
                              {payment.created_by && (
                                <div className="mt-1 pt-1 border-t border-gray-100">
                                  <p className="text-gray-500 text-[10px]">Recorded by: <span className="font-medium">{payment.created_by}</span></p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline View */}
        {viewMode === 'timeline' && (
          <div className="premium-card border-2 border-purple-200 p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3">Payment History Timeline</h3>
            
            {paidEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No payments recorded yet</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></div>
                
                <div className="space-y-4">
                  {paidEntries.map((entry, idx) => (
                    <div key={idx} className="relative pl-10">
                      {/* Timeline dot */}
                      <div className={`absolute left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        entry.entry_type === 'Bonus' 
                          ? 'bg-green-100 border-green-500' 
                          : 'bg-purple-100 border-purple-500'
                      }`}>
                        {entry.entry_type === 'Bonus' ? (
                          <Gift className="w-3 h-3 text-green-600" />
                        ) : (
                          <Check className="w-3 h-3 text-purple-600" />
                        )}
                      </div>
                      
                      <div className={`rounded-lg p-3 ${
                        entry.entry_type === 'Bonus' ? 'bg-green-50 border border-green-200' : 'bg-purple-50 border border-purple-200'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-semibold text-gray-800">Period {entry.period_index}</span>
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${
                              entry.entry_type === 'Bonus' ? 'bg-green-200 text-green-800' : 'bg-purple-200 text-purple-800'
                            }`}>
                              {entry.entry_type === 'Bonus' ? 'Bonus' : 'Payment'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {entry.paid_date 
                              ? formatDateOnlyIST(entry.paid_date)
                              : formatDateOnlyIST(entry.due_date)
                            }
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {entry.actual_amount_paid && (
                            <div>
                              <span className="text-gray-600">Amount:</span>
                              <span className="ml-1 font-semibold text-green-700">₹{entry.actual_amount_paid?.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                          {entry.actual_grams_credited && (
                            <div>
                              <span className="text-gray-600">Gold:</span>
                              <span className="ml-1 font-semibold text-amber-700">{entry.actual_grams_credited?.toFixed(3)}g</span>
                            </div>
                          )}
                          {entry.rate_used && (
                            <div>
                              <span className="text-gray-600">Rate:</span>
                              <span className="ml-1 font-medium">₹{entry.rate_used}/g</span>
                            </div>
                          )}
                          {entry.payment_type && (
                            <div>
                              <span className="text-gray-600">Mode:</span>
                              <span className="ml-1 font-medium">{entry.payment_type}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Revision History View */}
        {viewMode === 'revisions' && (
          <div className="premium-card border-2 border-indigo-200 p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3">🔄 Revision History</h3>
            
            {!enrollment.revision_history || enrollment.revision_history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No revisions recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {enrollment.revision_history.slice().reverse().map((revision, idx) => {
                  const isPaymentRecorded = revision.action === 'payment_recorded';
                  const isPaymentDeleted = revision.action === 'payment_deleted';
                  
                  return (
                    <div key={idx} className={`rounded-lg p-3 border-2 ${
                      isPaymentRecorded ? 'bg-green-50 border-green-300' : 
                      isPaymentDeleted ? 'bg-red-50 border-red-300' : 
                      'bg-blue-50 border-blue-300'
                    }`}>
                      {/* Header */}
                      <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              isPaymentRecorded ? 'bg-green-200 text-green-800' :
                              isPaymentDeleted ? 'bg-red-200 text-red-800' :
                              'bg-blue-200 text-blue-800'
                            }`}>
                              {isPaymentRecorded ? '✅ PAYMENT ADDED' : 
                               isPaymentDeleted ? '🗑️ PAYMENT DELETED' : 
                               revision.action.toUpperCase().replace('_', ' ')}
                            </span>
                            <span className="text-xs text-gray-600">Period {revision.period_index}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {formatDateIST(revision.timestamp)} • by {revision.user}
                          </div>
                        </div>
                      </div>
                      
                      {/* BEFORE & AFTER */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {/* BEFORE */}
                        <div className="bg-white rounded p-2 border border-gray-200">
                          <div className="font-bold text-gray-700 mb-1.5 text-center">⬅️ BEFORE</div>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className={`font-semibold ${
                                revision.before?.period_status === 'Paid' ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {revision.before?.period_status || 'Due'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount Paid:</span>
                              <span className="font-semibold">₹{(revision.before?.amount_paid || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {revision.before?.has_shortfall && (
                              <div className="flex justify-between text-red-600">
                                <span>Shortfall:</span>
                                <span className="font-semibold">₹{(revision.before?.shortfall_amount || 0).toLocaleString('en-IN')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* AFTER */}
                        <div className="bg-white rounded p-2 border border-gray-200">
                          <div className="font-bold text-gray-700 mb-1.5 text-center">➡️ AFTER</div>
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className={`font-semibold ${
                                revision.after?.period_status === 'Paid' ? 'text-green-600' : 'text-orange-600'
                              }`}>
                                {revision.after?.period_status || 'Due'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount Paid:</span>
                              <span className="font-semibold">₹{(revision.after?.amount_paid || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {revision.after?.has_shortfall && (
                              <div className="flex justify-between text-red-600">
                                <span>Shortfall:</span>
                                <span className="font-semibold">₹{(revision.after?.shortfall_amount || 0).toLocaleString('en-IN')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Transaction Details */}
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Type:</span>
                          <span className="font-semibold">{revision.payment_type || 'N/A'}</span>
                        </div>
                        {revision.details?.amount && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{isPaymentDeleted ? 'Amount Reversed:' : 'Amount:'}</span>
                            <span className="font-bold text-green-700">
                              ₹{(revision.details.amount || revision.details.amount_reversed || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Redeem Button */}
        {enrollment.status === 'Active' && enrollment.periods_paid === scheme?.periods_customer_pays && (
          <button
            onClick={handleRedeem}
            className="w-full py-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-lg font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <DollarSign className="w-5 h-5" />
            Redeem Enrollment
          </button>
        )}
      </div>

      {/* Payment Modals */}
      {showPaymentTypeSelector && (
        <PaymentTypeSelector
          isOpen={showPaymentTypeSelector}
          onClose={() => {
            setShowPaymentTypeSelector(false);
          }}
          onSelectType={(type) => {
            setShowPaymentTypeSelector(false);
            setActivePaymentModal(type);
          }}
        />
      )}

      {/* Payment Modals - Direct access without rate requirement */}

      <CashPaymentModal
        isOpen={activePaymentModal === 'cash'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        amount={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      <CardPaymentModal
        isOpen={activePaymentModal === 'card'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        amount={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      <UPIPaymentModal
        isOpen={activePaymentModal === 'upi'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        amount={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      <BankTransferModal
        isOpen={activePaymentModal === 'bank'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        amount={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      <OldGoldModal
        isOpen={activePaymentModal === 'old-gold'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        grossWeight={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      <MetalCreditModal
        isOpen={activePaymentModal === 'metal'}
        onClose={() => {
          setActivePaymentModal(null);
        }}
        onSave={handlePaymentSave}
        requiredAmount={selectedPeriod?.expected_amount || selectedPeriod?.due_amount || 0}
      />

      {/* WhatsApp Message Type Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Choose Message Type</h2>
            <p className="text-sm text-gray-600 mb-6">Select the type of message you want to send:</p>
            
            <div className="space-y-3">
              <button
                onClick={sendPaymentReminder}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">💰</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 group-hover:text-green-700">Payment Reminder</h3>
                    <p className="text-xs text-gray-600">Send reminder for next due payment</p>
                  </div>
                </div>
              </button>

              <button
                onClick={sendFullSummary}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="text-2xl">📋</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 group-hover:text-green-700">Full Summary</h3>
                    <p className="text-xs text-gray-600">Send complete enrollment details & schedule</p>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowWhatsAppModal(false)}
              className="w-full mt-4 px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Thermal Print Component */}
      {enrollment && customer && scheme && (
        <KittyThermalPrint 
          enrollment={enrollment} 
          customer={customer} 
          scheme={scheme} 
        />
      )}

      {/* Item Receipt Selector Modal */}
      <ItemReceiptSelector
        isOpen={showItemReceiptSelector}
        onClose={() => setShowItemReceiptSelector(false)}
        items={getOldGoldAndMetalCreditItems()}
        onPrintSelected={handlePrintSelectedItems}
      />

      {/* Item Receipt Print Components (Hidden from screen, only for printing) */}
      <div className="item-receipts-container">
        {getOldGoldAndMetalCreditItems().map((item, index) => (
          <ItemReceiptPrint
            key={index}
            item={item}
            itemIndex={index + 1}
            customer={customer}
            moduleRef={`Kitty: ${enrollment?.enrollment_id}`}
            transactionDate={item.payment_date || enrollment?.created_at}
            transactionId={item.payment_id}
          />
        ))}
      </div>
    </div>
  );
};

export default KittyEnrollmentDetail;
